import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  type Identifier,
} from "ra-core";
import { useCallback } from "react";

import { osloDateKey } from "../../misc/osloDate";
import { useInvalidate } from "../../misc/useInvalidate";

// «Fest øverst i dag» (kap. 4.5): nålene gjelder bare dagen de ble satt
// (Oslo-dato). En nål fra i går leses ikke, så raden går tilbake til sin
// plass ved midnatt uten noen opprydding.

export type DashboardPin = {
  id: Identifier;
  sales_id: Identifier;
  item_key: string;
  pinned_on: string;
  created_at: string;
};

export const usePins = () => {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const invalidate = useInvalidate();
  const today = osloDateKey();
  const me = identity?.id;
  const { data = [] } = useGetList<DashboardPin>(
    "dashboard_pins",
    {
      filter: { sales_id: me, pinned_on: today },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: me != null },
  );
  const todays = data
    .filter((p) => p.pinned_on === today && String(p.sales_id) === String(me))
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  const keys = todays.map((p) => p.item_key);

  /** Fester eller løsner raden. true = festet nå. */
  const toggle = useCallback(
    async (key: string) => {
      const existing = todays.find((p) => p.item_key === key);
      if (existing) {
        await dataProvider.delete("dashboard_pins", {
          id: existing.id,
          previousData: existing,
        });
      } else {
        await dataProvider.create("dashboard_pins", {
          data: {
            sales_id: me,
            item_key: key,
            pinned_on: today,
            created_at: new Date().toISOString(),
          },
        });
      }
      await invalidate("dashboard_pins");
      return !existing;
    },
    [todays, dataProvider, invalidate, me, today],
  );

  return { keys, toggle };
};
